import { useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { factionLinkSearch } from "../lib/faction-selection";

// The search keys the two per-hull surfaces share — /unit/<id> and /compare, the only routes
// that read a Loadout and a Level (ADR 0012). Declared once (review 2026-09-08): the two
// schemas had restated all five keys and the same paragraph about `level`'s spelling.
/** A numeric key's value as the router hands it over: a string or a number as it arrived, and
 * anything else JSON.parse made of the URL (`?level=true`, `?points=[0,0,1]`, `?level=null`)
 * handed on as the text it was, so the page REPORTS it as no level / no allocation rather than
 * silently reading Base with the token still in the URL (review 2026-09-10: a `.catch` here
 * dropped such a value before the notice could see it). */
const urlValue = z
  .unknown()
  .transform((value): string | number | undefined => {
    if (value === undefined || typeof value === "string" || typeof value === "number") return value;
    return JSON.stringify(value);
  })
  .optional();

export const leverSearch = {
  // `faction` is the site-wide Faction Selection (lib/faction-selection.ts, ADR 0010): declared
  // so the header's selection rides every link through the page. Neither surface filters by
  // it — an entity page shows one hull, and holding four Factions' hulls at once is what the
  // comparison is for.
  faction: z.string().optional().catch(undefined),
  // `corruption` is the Corruption Tier axis (ADR 0007): ?corruption=mild|moderate|extreme,
  // absent means uncorrupted.
  corruption: z.string().optional().catch(undefined),
  // `tech` is the whole research axis (lib/frame.ts, ADR 0009 + ADR 0011): absent is Base,
  // `all` is Fully-Teched, and a comma-separated list is that Tech Set's maximal elements.
  tech: z.string().optional().catch(undefined),
  // `items` is the Loadout (lib/loadout.ts, ADR 0012): the Ship Items equipped on ONE hull,
  // comma-separated ids. Declared on these two routes only — a Loadout belongs to one hull in
  // one place, so it rides no unit-level link and no roster view reads it. On /compare it
  // carries one `;` slot per column (ADR 0011), the grammar every frame key takes there.
  items: z.string().optional().catch(undefined),
  // `level` is the hull's experience Level (lib/level.ts, ADR 0012, Issue 49): 1 to 10, absent
  // being level 1. Declared on these two routes only, for the reason `items` is.
  //
  // A NUMBER OR A STRING, unlike every other key here, because it is the site's one numeric
  // key. The router parses search values with JSON.parse and stringifies them back the same
  // way, so `?level=10` arrives as the number 10 and must leave as one: a bare z.string()
  // caught it to undefined and dropped the key outright, and a z.coerce.string() re-wrote the
  // reader's URL to `?level=%2210%22` — both reproduced on the served build 2026-09-07. The
  // `;`-slotted comparison spelling (`10;;2`) is not a number and stays a string. What counts
  // as a level is lib/level.ts's rule; this only settles the spelling.
  level: urlValue,
  // `points` is the hull's Ability Points allocation (lib/ability-points.ts, ADR 0015,
  // Issue 60): one whole number per level-driven ability, in the game's order, absent being
  // nothing spent. Declared on these two routes only, for the reason `items` and `level` are.
  //
  // A NUMBER OR A STRING for the reason `level` is: an allocation that spells one position
  // ("2") is a bare number to the router's JSON.parse, while every comma- or `;`-bearing
  // spelling stays a string. What counts as an allocation is lib/ability-points.ts's rule;
  // this only settles the spelling.
  points: urlValue,
};

/** The site-wide Faction Selection as it rides the two per-hull surfaces' unit-level links
 * (ADR 0010). Neither page filters anything by it — an entity page shows one hull, and holding
 * four Factions' hulls at once is what the comparison is for — but a reader who said which
 * empire they are playing keeps saying it as they navigate. Declared once here beside the key
 * it reads (review 2026-09-08: /unit and /compare each held a byte-identical copy). Read
 * loosely, the way the palette reads the shell's search: both routes declare `faction` through
 * `leverSearch` above, so the loose read is exactly what either route's own hook returns. */
export function useFactionLink(): { faction?: string } {
  return factionLinkSearch(useSearch({ strict: false }));
}
